/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Html Dom Node 를 확장한다.
 *
 * @file /src/utils/Dom.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 8.
 */
import { EventMap } from '@utils/Events';
import { Language, LanguageText } from './Language';
import { Exceptions } from './Exceptions';

export class Dom<E extends HTMLElement = HTMLElement> {
    static elements: WeakMap<HTMLElement, Dom<HTMLElement>> = new WeakMap();
    static pointerListeners: Map<number, Dom> = new Map();

    readonly element: E | null;
    dataset: { [key: string]: any } = {};
    events: { [K in keyof EventMap]?: ((me: Dom<E>, e: EventMap[K]) => any)[] } = {};
    listeners: Map<string, WeakMap<Function, EventListener>> = new Map();
    computedStyle: CSSStyleDeclaration = null;

    /**
     * Dom 객체를 생성한다.
     *
     * @param element - HTML 엘리먼트
     */
    private constructor(element: E) {
        this.element = element;
    }

    /**
     * Dom 객체 재활용을 위한 static 생성자를 정의한다.
     *
     * @param element - HTML 엘리먼트
     * @returns Dom
     */
    static init<E extends HTMLElement = HTMLElement>(element: Node): Dom<E> | null {
        if (element instanceof HTMLElement) {
            if (Dom.elements.has(element) == true) {
                return Dom.elements.get(element) as Dom<E>;
            }

            const dom = new Dom<E>(element as E);
            Dom.elements.set(element, dom);

            return dom;
        } else {
            return null;
        }
    }

    /**
     * HTML 엘리먼트를 가져온다.
     *
     * @returns HTML 엘리먼트
     */
    getEl(): E {
        return this.element;
    }

    /**
     * HTML 엘리먼트의 Attribute 값을 설정한다.
     *
     * @param key - 설정할 Attribute키
     * @param value - 설정할 값
     * @returns Dom
     */
    setAttr(key: string, value: string): this {
        this.element?.setAttribute(key, value);
        return this;
    }

    /**
     * HTML 엘리먼트의 Attribute 값을 제거한다.
     *
     * @param key - 설정할 Attribute키
     * @returns Dom
     */
    removeAttr(key: string): this {
        this.element?.removeAttribute(key);
        return this;
    }

    /**
     * HTML 엘리먼트의 Attribute가 있는 경우 제거하고, 없는 경우 추가한다.
     *
     * @param key - 토글할 Attribute키
     * @param force - 강제 적용여부
     * @returns Dom
     */
    toggleAttr(key: string, force?: boolean): this {
        this.element?.toggleAttribute(key, force);
        return this;
    }

    /**
     * HTML 엘리먼트의 Attribute 값을 가져온다.
     *
     * @param key - 값을 가져올 Attribute 키
     * @returns 값
     */
    getAttr(key: string): string {
        return this.element?.getAttribute(key) ?? '';
    }

    /**
     * HTML 엘리먼트의 Data-Attribute 또는 Data 값을 설정한다.
     *
     * @param key - 값을 가져올 Data-Attribute 키
     * @param value - 값
     * @param is_dom - HTML 엘리먼트에 data-attribute 를 생성할 지 여부
     */
    setData(key: string, value: any, is_dom: boolean = true): this {
        this.dataset[key] = value;
        if (is_dom === true && (typeof value == 'string' || typeof value == 'number')) {
            this.setAttr('data-' + key, value.toString());
        }

        return this;
    }

    /**
     * HTML 엘리먼트의 Data-Attribute 또는 Data 값을 가져온다.
     *
     * @param key - 값을 가져올 Data 키
     * @returns 값
     */
    getData(key: string): any {
        if (this.dataset[key] === undefined) {
            return (
                this.element?.dataset[key] ??
                this.element?.dataset[key.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (_m, chr) => chr.toUpperCase())] ??
                null
            );
        } else {
            return this.dataset[key] ?? null;
        }
    }

    /**
     * HTML 엘리먼트의 부모요소를 가져온다.
     *
     * @returns parent
     */
    getParent(): Dom | null {
        if (this.element?.parentElement instanceof HTMLElement) {
            return Dom.init(this.element.parentElement);
        }

        return null;
    }

    /**
     * HTML 엘리먼트의 부모요소 트리에서 특정 부모요소를 가져온다.
     *
     * @param checker - 가져올 부모요소
     * @returns parent
     */
    getParents(checker: string): Dom | null {
        if (this.element == null) {
            return null;
        }

        const parent = this.element.closest(checker);
        if (parent instanceof HTMLElement) {
            return Dom.init(parent);
        }

        return null;
    }

    /**
     * HTML 엘리먼트의 자식요소를 가져온다.
     *
     * @returns children
     */
    getChildren(): Dom[] {
        if (this.element == null) {
            return [];
        }

        const children: Dom[] = [];
        Array.prototype.forEach.call(this.element.children, (item: HTMLElement) => {
            children.push(Dom.init(item));
        });

        return children;
    }

    /**
     * 현재 DOM 이 부모요소의 몇번째 자식요소인지 가져온다.
     *
     * @returns 인덱스
     */
    getIndex(): number {
        if (this.element == null) {
            return -1;
        }

        const children = this.element.parentElement.children ?? [];
        let index = 0;
        for (let i = 0, loop = children.length; i < loop; i++) {
            if (children[i].isEqualNode(this.element) == true) {
                return index;
            }
            if (children[i].nodeType == 1) index++;
        }

        return -1;
    }

    /**
     * HTML 엘리먼트가 특정 쿼리셀렉터에 일치하는지 확인한다.
     *
     * @param querySelector - 일치할지 확인할 쿼리셀럭터
     * @returns is_equal
     */
    is(querySelector: string): boolean {
        if (this.element == null) {
            return false;
        }

        return this.element.matches(querySelector);
    }

    /**
     * HTML 엘리먼트가 특정 DOM과 동일한지 확인한다.
     *
     * @param dom - 동일한지 여부를 확인할 DOM
     * @returns is_equal
     */
    isEqual(dom: Dom): boolean {
        if (this.element == null || dom.getEl() == null) {
            return false;
        }

        return this.element.isEqualNode(dom.getEl());
    }

    /**
     * HTML 엘리먼트가 특정 DOM과 일치하는지 확인한다.
     *
     * @param dom - 동일한지 여부를 확인할 DOM
     * @returns is_equal
     */
    isSame(dom: Dom): boolean {
        if (this.element == null || dom.getEl() == null) {
            return false;
        }

        return this.element.isSameNode(dom.getEl());
    }

    /**
     * 현재 정의된 모든 스타일을 가져온다.
     *
     * @returns styles
     */
    getStyles(): CSSStyleDeclaration {
        if (this.element === null) {
            return null;
        }

        if (this.computedStyle === null) {
            this.computedStyle = window.getComputedStyle(this.element);
        }

        return this.computedStyle;
    }

    /**
     * 스타일시트 등을 통해 현재 HTML 엘리먼트에 적용된 스타일을 가져온다.
     *
     * @param key - 가져올 스타일명
     * @param pseudo - ::before 또는 ::after
     * @returns 스타일값
     */
    getStyle(key: string, pseudo: string | null = null): string {
        if (this.element === null) return '';

        if (pseudo === null) {
            return this.getStyles().getPropertyValue(key);
        } else {
            return window.getComputedStyle(this.element, pseudo).getPropertyValue(key);
        }
    }

    /**
     * HTML 엘리먼트 스타일을 지정한다.
     *
     * @param key - 스타일명
     * @param value - 스타일값
     * @param is_important - !important 여부
     * @returns Dom
     */
    setStyle(key: string, value: string | number, is_important: boolean = false): this {
        if (this.element === null) return this;
        if (value === null) {
            this.element.style.removeProperty(key);
        } else {
            this.element.style.setProperty(key, value.toString(), is_important == true ? 'important' : null);
        }
        this.computedStyle = null;

        return this;
    }

    /**
     * HTML 엘리먼트 스타일을 여러개 지정한다.
     *
     * @param styles - css 형태의 key와 value를 가진 객체
     * @returns Dom
     */
    setStyles(styles: { [key: string]: string | number }): this {
        if (this.element === null) return this;

        this.element.style.cssText = Object.entries(styles)
            .map(([key, value]) => `${key}: ${value};`)
            .join(' ');

        return this;
    }

    /**
     * HTML 엘리먼트 스타일의 변수값을 지정한다.
     *
     * @param key - 변수명
     * @param value - 변수값
     * @returns Dom
     */
    setStyleProperty(key: string, value: string | number, priority?: string): this {
        this.element?.style.setProperty(key, value.toString(), priority);
        return this;
    }

    /**
     * HTML 엘리먼트의 너비(테두리 및 여백제외)를 가져온다.
     *
     * @returns width
     */
    getWidth(): number {
        if (!this.element) return 0;

        const style = this.getStyles();
        const padding = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);

        return Math.max(0, this.element.clientWidth - padding);
    }

    /**
     * HTML 엘리먼트의 높이(테두리 및 여백제외)를 가져온다.
     *
     * @returns height
     */
    getHeight(): number {
        if (!this.element) return 0;

        const style = this.getStyles();
        const padding = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);

        return Math.max(0, this.element.clientHeight - padding);
    }

    /**
     * HTML 엘리먼트의 너비(테두리제외)를 가져온다.
     *
     * @returns width
     */
    getInnerWidth(): number {
        if (!this.element) return 0;

        return this.element.clientWidth;
    }

    /**
     * HTML 엘리먼트의 높이(테두리제외)를 가져온다.
     *
     * @returns height
     */
    getInnerHeight(): number {
        if (!this.element) return 0;

        return this.element.clientHeight;
    }

    /**
     * HTML 엘리먼트의 너비(테두리 및 여백포함)를 가져온다.
     *
     * @param includeMargin - 외부간격(margin)을 포함할지 여부
     * @returns width
     */
    getOuterWidth(includeMargin: boolean = false): number {
        if (!this.element) return 0;

        let width = this.element.offsetWidth;

        if (includeMargin) {
            const style = this.getStyles();
            width += (parseFloat(style.marginLeft) || 0) + (parseFloat(style.marginRight) || 0);
        }
        return width;
    }

    /**
     * HTML 엘리먼트의 높이(테두리 및 여백포함)를 가져온다.
     *
     * @param includeMargin - 외부간격(margin)을 포함할지 여부
     * @returns height
     */
    getOuterHeight(includeMargin: boolean = false): number {
        if (!this.element) return 0;

        let height = this.element.offsetHeight;

        if (includeMargin) {
            const style = this.getStyles();
            height += (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
        }
        return height;
    }

    /**
     * HTML 엘리먼트의 스크롤 너비를 가져온다.
     *
     * @param is_border - 테두리굵기 포함여부
     * @returns scrollWidth
     */
    getScrollWidth(is_border: boolean = false): number {
        if (this.element == null) return 0;

        let border = 0;
        if (is_border == true) {
            const style = this.getStyles();
            border = parseFloat(style.borderLeftWidth) + parseFloat(style.borderRightWidth);
        }

        return this.element.scrollWidth + border;
    }

    /**
     * HTML 엘리먼트의 스크롤 높이를 가져온다.
     *
     * @param is_border - 테두리굵기 포함여부
     * @returns scrollHeight
     */
    getScrollHeight(is_border: boolean = false): number {
        if (this.element == null) return 0;

        let border = 0;
        if (is_border == true) {
            const style = this.getStyles();
            border = parseFloat(style.borderLeftWidth) + parseFloat(style.borderRightWidth);
        }

        return this.element.scrollHeight + border;
    }

    /**
     * HTML 엘리먼트의 문서 전체 기준으로 위치를 가져온다.
     *
     * @returns offset
     */
    getOffset(): { top: number; left: number } {
        if (this.element == null) return { top: 0, left: 0 };

        let rect = this.element.getBoundingClientRect();
        let view = this.element.ownerDocument.defaultView;
        return {
            top: rect.top + view.pageYOffset,
            left: rect.left + view.pageXOffset,
        };
    }

    /**
     * HTML 엘리먼트의 부모 객체 기준으로 위치를 가져온다.
     *
     * @param $target - 스크롤이 되는 기준 DOM
     * @returns offset
     */
    getPosition($target: Dom = null): { top: number; left: number } {
        if (this.element == null) return { top: 0, left: 0 };

        let marginTop = parseInt(this.getStyle('margin-top').replace(/px$/, ''));
        let marginLeft = parseInt(this.getStyle('margin-left').replace(/px$/, ''));

        if (this.getStyle('position') == 'fixed') {
            let offset = this.element.getBoundingClientRect();
            return { top: offset.top - marginTop, left: offset.left - marginLeft };
        }

        let offset = this.getOffset();
        let parentOffset = { top: 0, left: 0 };

        if ($target?.getEl()) {
            parentOffset = $target.getOffset();
            parentOffset.top += parseInt(this.getStyle('border-top-width')?.replace(/px$/, '') || '0');
            parentOffset.left += parseInt(this.getStyle('border-left-width')?.replace(/px$/, '') || '0');
        } else {
            const doc = this.element.ownerDocument;
            let offsetParent = this.element.offsetParent || doc.documentElement;

            while (
                offsetParent &&
                (offsetParent === doc.body || offsetParent === doc.documentElement) &&
                window.getComputedStyle(offsetParent).getPropertyValue('position') === 'static'
            ) {
                offsetParent = offsetParent.parentElement;
            }

            if (offsetParent instanceof HTMLElement && offsetParent !== this.element && offsetParent.nodeType === 1) {
                const $parent = Dom.init(offsetParent);
                parentOffset = $parent.getOffset();
                parentOffset.top += parseInt(this.getStyle('border-top-width')?.replace(/px$/, '') || '0');
                parentOffset.left += parseInt(this.getStyle('border-left-width')?.replace(/px$/, '') || '0');
            }
        }

        return {
            top: offset.top - parentOffset.top - marginTop,
            left: offset.left - parentOffset.left - marginLeft,
        };
    }

    /**
     * 현재 객체의 상대적인 위치정보를 가져온다.
     *
     * @returns rect
     */
    async getRect(): Promise<DOMRect | null> {
        if (this.element === null) {
            return null;
        }

        return new Promise((resolve) => {
            requestAnimationFrame(() => {
                resolve(this.element.getBoundingClientRect());
            });
        });
    }

    /**
     * HTML 엘리먼트의 스크롤 위치를 가져온다.
     *
     * @returns scroll
     */
    getScroll(): { left: number; top: number } {
        if (this.element == null) return { left: 0, top: 0 };
        return { left: this.element.scrollLeft, top: this.element.scrollTop };
    }

    /**
     * HTML 엘리먼트의 스크롤 위치를 설정한다.
     *
     * @param left - 좌측위치 (NULL 인경우 이동하지 않음)
     * @param top - 상단위치 (NULL 인경우 이동하지 않음)
     * @param animate - 애니메이션 여부
     */
    setScroll(left: number = null, top: number = null, animate: boolean = true): void {
        if (this.element == null) return;

        let options: ScrollToOptions = {
            behavior: animate === true ? 'smooth' : 'auto',
        };
        if (left !== null) options.left = left;
        if (top !== null) options.top = top;
        this.element.scroll(options);
    }

    /**
     * HTML 엘리먼트에 스타일시트(class)를 추가한다.
     *
     * @param className - 추가할 클래스명
     * @returns Dom
     */
    addClass(...className: string[]): this {
        this.element?.classList.add(...className);
        return this;
    }

    /**
     * HTML 엘리먼트의 스타일시트(class)를 제거한다.
     *
     * @param className - 제거할 클래스명
     * @returns Dom
     */
    removeClass(...className: string[]): this {
        if (className.length == 0) {
            this.element?.classList.remove(...this.element?.classList);
        } else {
            this.element?.classList.remove(...className);
        }
        return this;
    }

    /**
     * HTML 엘리먼트의 스타일시트(class)가 있는 경우 제거하고, 없는 경우 추가한다.
     *
     * @param className - 토글할 클래스명
     * @param force - 강제 적용여부
     * @returns Dom
     */
    toggleClass(className: string, force?: boolean): this {
        this.element?.classList.toggle(className, force);
        return this;
    }

    /**
     * HTML 엘리먼트에 스타일시트(class)가 존재하는지 확인한다.
     *
     * @param className - 확인할 클래스명
     * @returns has_class
     */
    hasClass(className: string): boolean {
        return this.element?.classList.contains(className) ?? false;
    }

    /**
     * HTML 엘리먼트에 텍스트를 추가한다.
     *
     * @param text - 추가할 텍스트명
     * @returns Dom
     */
    text(text: string): this {
        if (this.element === null) return this;
        this.element.textContent = text;
        return this;
    }

    /**
     * HTML 엘리먼트에 HTML 태그를 추가한다.
     *
     * @param html - 추가할 HTML
     * @param is_diff - 기존 내용과 비교하여 변경된 부분만 대치할지 여부(기본값 false)
     * @returns Dom
     */
    html(html: LanguageText, is_diff: boolean = false): this {
        if (this.element === null) {
            return this;
        }

        if (html instanceof Promise || (typeof html == 'object' && typeof html.getText == 'function')) {
            Language.resolveText(html).then((text) => {
                if (is_diff == true) {
                    const template = document.createElement('template');
                    template.innerHTML = text;
                    Dom.diff(this.element, template.content);
                } else {
                    this.element.innerHTML = text;
                }
            });
        } else {
            if (is_diff == true) {
                const template = document.createElement('template');
                template.innerHTML = html.toString();
                Dom.diff(this.element, template.content);
            } else {
                this.element.innerHTML = html.toString();
            }
        }

        return this;
    }

    /**
     * Dom 객체를 HTML 코드로 변환한다.
     *
     * @returns html
     */
    toHtml(is_inner_html: boolean = false): string {
        if (is_inner_html === true) {
            return this.element?.innerHTML ?? '';
        } else {
            return this.element?.outerHTML ?? '';
        }
    }

    /**
     * HTML 엘리먼트가 INPUT, TEXTAREA, SELECT 요소인 경우 값을 지정한다.
     *
     * @param value - 지정할 값
     * @param is_fire_event - 이벤트발생여부
     * @returns Dom
     */
    setValue(value: string | boolean, is_fire_event: boolean = true): this {
        if (
            this.element instanceof HTMLInputElement ||
            this.element instanceof HTMLTextAreaElement ||
            this.element instanceof HTMLSelectElement
        ) {
            if (typeof value == 'string') {
                value = value.normalize('NFC');
            }

            const originValue = this.getValue();
            if (this.element.getAttribute('type') == 'checkbox' || this.element.getAttribute('type') == 'radio') {
                if (typeof value === 'boolean') {
                    (this.element as HTMLInputElement).checked = value;
                } else {
                    (this.element as HTMLInputElement).checked = this.element.getAttribute('value') == value;
                }
            } else if (typeof value == 'string') {
                this.element.value = value;
            }

            if (is_fire_event == true && originValue !== this.getValue()) {
                this.trigger('change');
            }
        } else {
            Exceptions.error('HTMLElement is not HTMLInputElement');
        }
        return this;
    }

    /**
     * HTML 엘리먼트가 INPUT, TEXTAREA, SELECT 요소인 경우 값을 가져온다.
     *
     * @returns 값
     */
    getValue(): string | null {
        if (
            this.element instanceof HTMLInputElement ||
            this.element instanceof HTMLTextAreaElement ||
            this.element instanceof HTMLSelectElement
        ) {
            if (this.element.getAttribute('type') == 'checkbox' || this.element.getAttribute('type') == 'radio') {
                if ((this.element as HTMLInputElement).checked == true) {
                    return this.element.value.normalize('NFC');
                } else {
                    return null;
                }
            } else {
                return this.element.value.normalize('NFC');
            }
        }
    }

    /**
     * HTML 엘리먼트가 RADIO, CHECKBOX 인 경우 선택여부를 가져온다.
     *
     * @returns checked
     */
    isChecked(): boolean {
        if (this.element instanceof HTMLInputElement) {
            if (this.element.getAttribute('type') == 'checkbox' || this.element.getAttribute('type') == 'radio') {
                return this.element.checked;
            }
        } else {
            return false;
        }
    }

    /**
     * HTML 엘리먼트가 INPUT, TEXTAREA, SELECT 요소인 경우 값을 초기화한다.
     *
     * @returns Dom
     */
    reset(): this {
        if (this.element instanceof HTMLInputElement) {
            if (this.element.getAttribute('type') == 'checkbox' || this.element.getAttribute('type') == 'radio') {
                (this.element as HTMLInputElement).checked = this.element.getAttribute('checked') ? true : false;
            } else {
                this.element.value = this.element.getAttribute('value') ? this.element.getAttribute('value') : null;
            }
        } else if (this.element instanceof HTMLTextAreaElement) {
            this.element.value = this.element.innerHTML ? this.element.innerHTML : null;
        }

        return this;
    }

    /**
     * HTML 엘리먼트의 제일 처음에 Dom 을 추가한다.
     *
     * @param child - 추가할 Dom 객체
     * @returns Dom
     */
    prepend(child: Dom): this {
        this.element?.prepend(child.getEl());
        return this;
    }

    /**
     * HTML 엘리먼트의 제일 마지막에 Dom 을 추가한다.
     *
     * @param child - 추가할 Dom 객체
     * @param position - 추가할 위치 (NULL 인 경우 제일 마지막 위치)
     * @returns Dom
     */
    append(child: Dom | Dom[] | DocumentFragment, position: number = null): this {
        if (child instanceof Array) {
            child.forEach((item: Dom) => {
                this.append(item, position === null || position < 0 ? position : position++);
            });
        } else if (child instanceof Dom) {
            if (position === null || position >= (this.element?.children.length ?? 0)) {
                this.element?.append(child.getEl());
            } else if (position < 0 && Math.abs(position) >= (this.element?.children.length ?? 0)) {
                this.element?.prepend(child.getEl());
            } else if (position < 0) {
                this.element?.insertBefore(
                    child.getEl(),
                    this.element?.children[this.element?.children.length + position]
                );
            } else {
                this.element?.insertBefore(child.getEl(), this.element?.children[position]);
            }
        } else if (child instanceof DocumentFragment) {
            this.element?.append(child);
        }

        return this;
    }

    /**
     * HTML 엘리먼트의 target 엘리먼트 이전에 추가한다.
     *
     * @param child - 추가할 Dom 객체
     * @param target - 타겟 Dom 객체
     * @returns Dom
     */
    insertBefore(child: Dom, target: Dom | Node = null): this {
        if (this.element !== null) {
            if (target instanceof Dom) {
                this.element.insertBefore(child.getEl(), target.getEl());
            } else {
                this.element.insertBefore(child.getEl(), target);
            }
        }

        return this;
    }

    /**
     * HTML 엘리먼트의 target 엘리먼트 이후에 추가한다.
     * target 이 마지막 자식이거나 null 인 경우 끝에 추가한다.
     *
     * @param child - 추가할 Dom 객체
     * @param target - 타겟 Dom 객체
     * @returns Dom
     */
    insertAfter(child: Dom, target: Dom | Node = null): this {
        if (this.element !== null) {
            if (target instanceof Dom) {
                this.element.insertBefore(child.getEl(), target.getEl().nextSibling);
            } else if (target !== null) {
                this.element.insertBefore(child.getEl(), (target as Node).nextSibling);
            } else {
                this.element.appendChild(child.getEl());
            }
        }

        return this;
    }

    /**
     * HTML 엘리먼트를 다른 Dom 으로 교체한다.
     *
     * @param replacement - 교체할 Dom 객체
     * @returns Dom
     */
    replaceWith(replacement: Dom): this {
        if (this.element !== null) {
            this.element.replaceWith(replacement.getEl());
        }
        return this;
    }

    /**
     * 특정 Dom 이 현재 Dom 의 상위 Dom 인지 확인한다.
     *
     * @param parent - 확인할 Dom 객체
     * @returns has_parent
     */
    hasParent(parent: Dom): boolean {
        if (this.element == null || (parent?.getEl() ?? null) === null) return false;
        return parent.getEl().contains(this.element);
    }

    /**
     * 특정 Dom 이 현재 Dom 의 하위 Dom 인지 확인한다.
     *
     * @param child - 확인할 Dom 객체
     * @returns has_child
     */
    hasChild(child: Dom): boolean {
        if (this.element == null || (child?.getEl() ?? null) === null) return false;
        return this.element.contains(child.getEl());
    }

    /**
     * 현재 Dom 의 이전 Dom 을 가져온다.
     *
     * @param selector - 다음에 위치한 Dom 중 찾을 selector (NULL 인 경우 바로 다음 Dom 을 반환한다.)
     * @returns prev
     */
    prev(selector: string = null): Dom | null {
        let current: HTMLElement = this.element;
        while (current !== null) {
            let prev = current.previousElementSibling;
            if (prev instanceof HTMLElement) {
                if (selector === null) {
                    return Dom.init(prev);
                } else {
                    if (Dom.init(prev).is(selector) == true) {
                        return Dom.init(prev);
                    }

                    current = prev;
                }
            } else {
                return null;
            }
        }

        return null;
    }

    /**
     * 현재 Dom 의 다음 Dom 을 가져온다.
     *
     * @param selector - 다음에 위치한 Dom 중 찾을 selector (NULL 인 경우 바로 다음 Dom 을 반환한다.)
     * @returns next
     */
    next(selector: string = null): Dom | null {
        let current: HTMLElement = this.element;
        while (current !== null) {
            let next = current.nextElementSibling;
            if (next instanceof HTMLElement) {
                if (selector === null) {
                    return Dom.init(next);
                } else {
                    if (Dom.init(next).is(selector) == true) {
                        return Dom.init(next);
                    }

                    current = next;
                }
            } else {
                return null;
            }
        }

        return null;
    }

    /**
     * HTML 엘리먼트에 포커스를 지정한다.
     */
    focus(): void {
        this.element?.focus();
    }

    /**
     * HTML 엘리먼트 위치로 스크롤을 이동한다.
     *
     * @param animate - 애니메이션여부
     * @param $target - 스크롤이 되는 기준 DOM
     */
    scrollTo(
        animate: boolean = true,
        $target: Dom = null,
        padding: { top?: number; bottom?: number; left?: number; right?: number } = null
    ): void {
        $target ??= this.getScrollable();
        if (!$target) return;

        const is_root = $target.is(':root') == true || $target.is('body') == true;

        const position = this.getPosition($target);
        const scroll = $target.getScroll();

        const elementTop = position.top;
        const elementBottom = position.top + this.getOuterHeight();

        let viewTop = 0;
        let viewBottom = $target.getInnerHeight();

        if (is_root == true) {
            viewTop += $target.getOffset().top + scroll.top;
            viewBottom += $target.getOffset().top + scroll.top;
        }

        const topPadding = padding?.top ?? 0;
        const bottomPadding = padding?.bottom ?? 0;

        let move = { top: null, left: null };

        if (elementTop < viewTop + topPadding) {
            move.top = scroll.top + (elementTop - viewTop) - topPadding;
        } else if (elementBottom > viewBottom - bottomPadding) {
            move.top = scroll.top + (elementBottom - viewBottom) + bottomPadding;
        }

        const elementLeft = position.left;
        const elementRight = position.left + this.getOuterWidth();

        let viewLeft = 0;
        let viewRight = $target.getInnerWidth();

        if (is_root == true) {
            viewLeft += $target.getOffset().left + scroll.left;
            viewRight += $target.getOffset().left + scroll.left;
        }

        const leftPadding = padding?.left ?? 0;
        const rightPadding = padding?.right ?? 0;

        if (elementLeft <= viewLeft + leftPadding) {
            move.left = scroll.left + (elementLeft - viewLeft) - leftPadding;
        } else if (elementRight > viewRight - rightPadding) {
            move.left = scroll.left + (elementRight - viewRight) + rightPadding;
        }

        if (move.top !== null || move.left !== null) {
            $target.getEl().scrollTo({
                left: move.left ?? scroll.left,
                top: move.top ?? scroll.top,
                behavior: animate ? 'smooth' : 'auto',
            });
        }
    }

    /**
     * 현재 Dom 부터 상위를 탐색하며 스크롤이 되는 Dom을 가져온다.
     *
     * @param $start 시작 Dom
     * @returns 스크롤이 되는 Dom
     */
    getScrollable($start: Dom = null): Dom {
        $start ??= this;
        while ($start !== null) {
            if ($start.isScrollable() == true) {
                return $start;
            }

            $start = $start.getParent();

            if ($start.getEl().tagName.toLowerCase() === 'html') {
                return $start;
            }
        }

        return null;
    }

    /**
     * 스크롤 여부 판단
     * @returns 스크롤 여부
     */
    isScrollable(): boolean {
        const overflow = this.getStyle('overflow');
        const overflowX = this.getStyle('overflowX');
        const overflowY = this.getStyle('overflowY');

        if (
            overflow === 'auto' ||
            overflow === 'scroll' ||
            overflowX === 'auto' ||
            overflowX === 'scroll' ||
            overflowY === 'auto' ||
            overflowY === 'scroll' ||
            this.getScrollWidth() > this.getOuterWidth() ||
            this.getScrollHeight() > this.getOuterHeight()
        ) {
            return true;
        }

        return false;
    }

    /**
     * HTML 엘리먼트에 포커스를 해제한다.
     */
    blur(): void {
        this.element?.blur();
    }

    /**
     * HTML 엘리먼트를 보인다.
     * 자바스크립트를 통해 숨겨진 뒤 다시 보이는 경우 원래의 display 속성을 사용하고,
     * 그렇지 않은 경우 설정된 display 속성값으로 객체를 보인다.
     *
     * @param display - 초기 display 속성 (기본값 : block)
     */
    show(display: string = 'block'): void {
        if (this.element === null) return;

        if (this.element.style.getPropertyValue('display') === 'none') {
            this.element.style.removeProperty('display');
        } else {
            this.setStyle('display', this.getData('origin-display') ?? display);
        }
    }

    /**
     * HTML 엘리먼트를 숨긴다.
     */
    hide(): void {
        if (this.element === null) return;
        if (this.getStyle('display') == 'none') return;

        if (this.getStyle('display') != 'none' && this.getStyle('display') != 'block') {
            this.setData('origin-display', this.getStyle('display'));
        }
        this.setStyle('display', 'none');
    }

    /**
     * HTML 엘리먼트를 제거한다.
     */
    remove(): void {
        this.element?.remove();
    }

    /**
     * HTML 엘리먼트에 애니메이션을 추가한다.
     *
     * @param keyFrames - 키프레임
     * @param options - 옵션
     * @param finish - 애니메이션 완료시 실행할 콜백함수
     * @returns 애니메이션 객체
     */
    animate(
        keyFrames: Keyframe[] | PropertyIndexedKeyframes,
        options?: number | KeyframeAnimationOptions,
        finish?: EventListener
    ): Animation {
        this.stop();
        const animate = this.element?.animate(keyFrames, options);
        if (finish) {
            animate.addEventListener('finish', finish);
        }

        return animate;
    }

    /**
     * HTML 엘리먼트의 재생중인 애니메이션을 중단한다.
     */
    stop(): void {
        this.element?.getAnimations()?.forEach((animate: Animation) => {
            if (animate.constructor.name !== 'Animation') return;
            try {
                animate.commitStyles();
            } catch {}
            animate.cancel();
        });
    }

    /**
     * HTML 엘리먼트를 좌우로 흔든다.
     *
     * @param times - 반복횟수(기본값 : 4)
     * @param duration - 애니메이션시각(기본값 : 800)
     * @param distance - 이동거리(기본값 : 10)
     */
    shake(times: number = 4, duration: number = 800, distance: number = 10): void {
        const keyFrames: Keyframe[] = [];
        for (let i = 0; i < times; i++) {
            keyFrames.push({ transform: 'translateX(' + distance + 'px)' });
            keyFrames.push({ transform: 'translateX(' + distance * -1 + 'px)' });
        }
        keyFrames.push({ transform: 'translateX(0px)' });
        this.element?.animate(keyFrames, { duration: duration, easing: 'ease-out' });
    }

    /**
     * HTML 엘리먼트에 이벤트를 추가한다.
     *
     * @param name - 추가할 이벤트명
     * @param listener - 이벤트리스너
     * @param options - 이벤트리스너 옵션
     * @returns Dom
     */
    on<K extends keyof EventMap & string>(
        name: K,
        listener: (me: Dom<E>, e: EventMap[K]) => any,
        options?: boolean | AddEventListenerOptions
    ): this {
        this.events[name] ??= [];
        this.events[name].push(listener);
        let wrapped: EventListener;

        switch (name) {
            case 'hover':
                wrapped = (e: MouseEvent) => {
                    listener(this, e);
                    e.preventDefault();
                    e.stopImmediatePropagation();
                };
                this.element?.addEventListener('mouseenter', wrapped);
                this.element?.addEventListener('mouseleave', wrapped);
                break;

            case 'longpress':
                wrapped = (e: PointerEvent) => {
                    if (e.pointerType == 'touch' || e.pointerType == 'pen') {
                        this.setData('longpress', {
                            x: e.clientX,
                            y: e.clientY,
                            timeout: setTimeout(() => {
                                e.stopImmediatePropagation();
                                e.preventDefault();
                                e.stopPropagation();
                                (listener as (me: Dom<E>, e: PointerEvent) => any)(this, e);
                            }, 1000),
                            cancel: (e: PointerEvent) => {
                                const longpress = this.getData('longpress') ?? null;
                                if (e.type == 'pointermove') {
                                    const diffX = Math.abs(longpress.x - (e as PointerEvent).clientX);
                                    const diffY = Math.abs(longpress.y - (e as PointerEvent).clientY);
                                    if (diffX < 10 && diffY < 10) {
                                        return;
                                    }
                                }

                                clearTimeout(longpress.timeout);
                                Dom.pointerListeners.delete((e as PointerEvent).pointerId);
                                this.setData('longpress', null);
                            },
                        });
                        Dom.pointerListeners.set(e.pointerId, this);
                    }
                };
                this.element?.addEventListener('pointerdown', wrapped);
                break;

            default:
                wrapped = (ev) => listener(this, ev as EventMap[K]);
                this.element?.addEventListener(name, wrapped, options);
        }

        if (this.listeners.has(name) == false) {
            this.listeners.set(name, new WeakMap());
        }
        this.listeners.get(name).set(listener, wrapped);

        return this;
    }

    /**
     * HTML 엘리먼트에 이벤트를 제거한다.
     *
     * @param name - 제거할 이벤트명
     * @param listener - 이벤트리스너
     * @returns Dom
     */
    off<K extends keyof EventMap & string>(name: K, listener: (me: Dom<E>, e: EventMap[K]) => any): this {
        const index = (this.events[name] ?? []).findIndex((e) => e === listener);
        if (index > -1) {
            this.events[name].splice(index, 1);
        }

        const wrapped = this.listeners.get(name)?.get(listener);
        if (wrapped) {
            switch (name) {
                case 'hover':
                    this.element?.removeEventListener('mouseenter', wrapped);
                    this.element?.removeEventListener('mouseleave', wrapped);
                    break;

                case 'longpress':
                    this.element?.removeEventListener('pointerdown', wrapped);
                    break;

                default:
                    this.element?.removeEventListener(name, wrapped);
            }

            this.listeners.get(name).delete(listener);
        }

        return this;
    }

    /**
     * 이벤트를 발생시킨다.
     *
     * @param name - 발생시킬 이벤트명
     * @param e - 원본 이벤트객체
     */
    trigger(name: string, e: Event = null): void {
        if (this.getAttr('disabled') == 'disabled') {
            return;
        }

        if (name == 'pointermove' || name == 'pointerup' || name == 'pointercancel') {
            this.getData('longpress')?.cancel(e);
        }

        this.element?.dispatchEvent(new Event(name));
    }

    /**
     * HTML 엘리먼트의 모든 하위요소를 제거한다.
     */
    empty(): void {
        if (this.element == null) return;
        this.element.innerHTML = '';
    }

    /**
     * HTML 엘리먼트가 비었는지 확인한다.
     *
     * @returns is_empty
     */
    isEmpty(): boolean {
        return this.element?.hasChildNodes() === false;
    }

    /**
     * HTML 엘리먼트가 숨겨진 상태인지 확인한다.
     *
     * @returns is_hidden
     */
    isHidden(): boolean {
        if (this.element === null) {
            return true;
        }

        return this.getStyle('display') === 'none';
    }

    /**
     * HTML 엘리먼트가 document 객체에 직간접적으로 연결된 상태인지 확인한다.
     *
     * @returns is_connected
     */
    isConnected(): boolean {
        if (this.element === null) {
            return false;
        }

        return this.element.isConnected;
    }

    /**
     * HTML 엘리먼트의 비활성 여부를 설정한다.
     *
     * @param disabled - 비활성화여부
     */
    setDisabled(disabled: boolean): void {
        if (disabled == true) {
            this.setAttr('disabled', 'disabled');
        } else {
            this.removeAttr('disabled');
        }
    }

    /**
     * HTML 엘리먼트를 활성화한다.
     */
    enable(): void {
        this.setDisabled(false);
        if (this.hasClass('loading') == true) {
            this.removeClass('loading');
        }
    }

    /**
     * HTML 엘리먼트를 비활성화한다.
     *
     * @param is_loading - 로딩 인디케이터를 보일지 여부
     */
    disable(is_loading: boolean = false): void {
        this.setDisabled(true);
        if (is_loading == true) {
            this.addClass('loading');
        }
    }

    /**
     * HTML 엘리먼트를 복제한다.
     *
     * @param is_event_listeners - 이벤트 리스너를 복제할지 여부
     * @returns clone
     */
    clone(is_event_listeners: boolean = false): Dom<E> {
        const element = this.element.cloneNode(true);
        const clone = Dom.init<E>(element);

        if (is_event_listeners == true) {
            this.cloneEvent(this, clone);
            const children = clone.getEl().querySelectorAll('*');
            this.element.querySelectorAll('*').forEach((element: HTMLElement, index: number) => {
                const origin = Dom.init(element);
                const clone = Dom.init(children[index] as HTMLElement);
                this.cloneEvent(origin, clone);
            });
        }

        return clone;
    }

    /**
     * HTML 엘리먼트의 이벤트를 복제한다.
     *
     * @param origin - 원본
     * @param clone - 복제본
     */
    cloneEvent(origin: Dom, clone: Dom) {
        for (const name in this.events) {
            for (const listener of origin.events[name]) {
                clone.on(name, listener);
            }
        }
    }

    /**
     * 두 노드의 자식들을 비교하여 변경된 부분만 치환한다.
     *
     * @param target - 변경 대상 노드
     * @param source - 비교할 노드
     */
    private static diff(target: Node, source: Node): void {
        const oldChildren = Array.from(target.childNodes);
        const newChildren = Array.from(source.childNodes);
        const maxLen = Math.max(oldChildren.length, newChildren.length);

        for (let i = 0; i < maxLen; i++) {
            const oldChild = oldChildren[i];
            const newChild = newChildren[i];

            if (!oldChild && newChild) {
                target.appendChild(newChild.cloneNode(true));
            } else if (oldChild && !newChild) {
                target.removeChild(oldChild);
            } else if (oldChild.nodeType !== newChild.nodeType || oldChild.nodeName !== newChild.nodeName) {
                target.replaceChild(newChild.cloneNode(true), oldChild);
            } else if (oldChild.nodeType === Node.TEXT_NODE) {
                if (oldChild.textContent !== newChild.textContent) {
                    oldChild.textContent = newChild.textContent;
                }
            } else if (oldChild instanceof HTMLElement && newChild instanceof HTMLElement) {
                // 새 속성 추가 또는 변경
                for (const attr of Array.from(newChild.attributes)) {
                    if (oldChild.getAttribute(attr.name) !== attr.value) {
                        oldChild.setAttribute(attr.name, attr.value);
                    }
                }

                // 제거된 속성 삭제
                for (const attr of Array.from(oldChild.attributes)) {
                    if (!newChild.hasAttribute(attr.name)) {
                        oldChild.removeAttribute(attr.name);
                    }
                }

                Dom.diff(oldChild, newChild);
            }
        }
    }
}
